import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  GeoPoint,
  setDoc
} from 'firebase/firestore';
import { db } from './firebase';

// Types
export interface User {
  id?: string;
  profile: {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    userType: 'buyer' | 'seller' | 'broker' | 'investor' | 'admin';
    avatar: string;
    brokerageName?: string;
    location: {
      city: string;
      province: string;
      country: string;
      coordinates: GeoPoint;
    };
    preferences: {
      categories: string[];
      priceRange: { min: number; max: number };
      locations: string[];
      emailNotifications: boolean;
    };
    verification: {
      emailVerified: boolean;
      phoneVerified: boolean;
      identityVerified: boolean;
      brokerLicense?: string;
    };
    createdAt: Timestamp;
    updatedAt: Timestamp;
    lastActive: Timestamp;
  };
  stats: {
    listingsCount: number;
    savedListingsCount: number;
    messagesCount: number;
  };
  savedListings?: Array<{
    businessId: string;
    notes?: string;
    tags?: string[];
    savedAt: Timestamp;
  }>;
  settings: {
    privacy: {
      showContactInfo: boolean;
      showProfile: boolean;
      allowMessages: boolean;
    };
    notifications: {
      email: boolean;
      push: boolean;
      sms: boolean;
      marketing: boolean;
    };
    language: string;
  };
}

export interface UserFilters {
  userType?: 'buyer' | 'seller' | 'broker' | 'investor' | 'admin' | 'buyer_seller';
  province?: string;
  city?: string;
  verified?: boolean;
  active?: boolean;
}

// User Service Class
class UserService {
  private collection = 'users';

  // Create a new user
  async createUser(userId: string, userData: Omit<User, 'id' | 'profile.createdAt' | 'profile.updatedAt' | 'profile.lastActive' | 'stats'>): Promise<string> {
    try {
      const now = Timestamp.now();
      
      // Prepare verification object, only including brokerLicense if it has a value
      const verification = {
        emailVerified: userData.profile.verification?.emailVerified || false,
        phoneVerified: userData.profile.verification?.phoneVerified || false,
        identityVerified: userData.profile.verification?.identityVerified || false,
        ...(userData.profile.verification?.brokerLicense && { brokerLicense: userData.profile.verification.brokerLicense })
      };

      // Ensure coordinates are properly formatted
      const location = {
        ...userData.profile.location,
        coordinates: userData.profile.location.coordinates || new GeoPoint(0, 0)
      };

      const userWithDefaults: Omit<User, 'id'> = {
        ...userData,
        profile: {
          ...userData.profile,
          location,
          createdAt: now,
          updatedAt: now,
          lastActive: now,
          verification
        },
        stats: {
          listingsCount: 0,
          savedListingsCount: 0,
          messagesCount: 0
        },
        savedListings: [],
        settings: {
          privacy: {
            showContactInfo: true,
            showProfile: true,
            allowMessages: true
          },
          notifications: {
            email: true,
            push: true,
            sms: false,
            marketing: false
          },
          language: 'en'
        }
      };

      const docRef = doc(db, this.collection, userId);
      await setDoc(docRef, userWithDefaults);
      return userId;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  // Get a single user by ID
  async getUser(id: string): Promise<User | null> {
    try {
      const docRef = doc(db, this.collection, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as User;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  }

  // Get user by email
  async getUserByEmail(email: string): Promise<User | null> {
    try {
      const q = query(
        collection(db, this.collection),
        where('profile.email', '==', email)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() } as User;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting user by email:', error);
      throw error;
    }
  }

  // Get all users (for admin)
  async getAllUsers(): Promise<User[]> {
    try {
      const q = query(
        collection(db, this.collection),
        orderBy('profile.createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      
      const users: User[] = [];
      querySnapshot.forEach((doc) => {
        users.push({ id: doc.id, ...doc.data() } as User);
      });

      return users;
    } catch (error) {
      console.error('Error getting all users:', error);
      throw error;
    }
  }

  // Get users with filtering
  async getUsers(filters: UserFilters = {}): Promise<User[]> {
    try {
      let q = collection(db, this.collection);
      const constraints: any[] = [];

      // Apply filters
      if (filters.userType) {
        constraints.push(where('profile.userType', '==', filters.userType));
      }
      if (filters.province) {
        constraints.push(where('profile.location.province', '==', filters.province));
      }
      if (filters.city) {
        constraints.push(where('profile.location.city', '==', filters.city));
      }
      if (filters.verified !== undefined) {
        constraints.push(where('profile.verification.identityVerified', '==', filters.verified));
      }

      // Apply sorting
      constraints.push(orderBy('profile.createdAt', 'desc'));

      const querySnapshot = await getDocs(query(q, ...constraints));
      
      const users: User[] = [];
      querySnapshot.forEach((doc) => {
        users.push({ id: doc.id, ...doc.data() } as User);
      });

      return users;
    } catch (error) {
      console.error('Error getting users:', error);
      throw error;
    }
  }

  // Update a user
  async updateUser(id: string, updates: Partial<User>): Promise<void> {
    try {
      const docRef = doc(db, this.collection, id);
      const updateData = {
        ...updates,
        'profile.updatedAt': Timestamp.now(),
        'profile.lastActive': Timestamp.now()
      };
      
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  // Delete a user
  async deleteUser(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.collection, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // Update user stats
  async updateUserStats(userId: string, stats: Partial<User['stats']>): Promise<void> {
    try {
      const docRef = doc(db, this.collection, userId);
      await updateDoc(docRef, {
        stats: stats,
        'profile.lastActive': Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating user stats:', error);
      throw error;
    }
  }

  // Increment user stat
  async incrementUserStat(userId: string, statField: keyof User['stats'], increment: number = 1): Promise<void> {
    try {
      const docRef = doc(db, this.collection, userId);
      await updateDoc(docRef, {
        [`stats.${statField}`]: increment,
        'profile.lastActive': Timestamp.now()
      });
    } catch (error) {
      console.error('Error incrementing user stat:', error);
      throw error;
    }
  }

  // Update user preferences
  async updateUserPreferences(userId: string, preferences: Partial<User['profile']['preferences']>): Promise<void> {
    try {
      const docRef = doc(db, this.collection, userId);
      await updateDoc(docRef, {
        'profile.preferences': preferences,
        'profile.updatedAt': Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating user preferences:', error);
      throw error;
    }
  }

  // Update user settings
  async updateUserSettings(userId: string, settings: Partial<User['settings']>): Promise<void> {
    try {
      const docRef = doc(db, this.collection, userId);
      await updateDoc(docRef, {
        settings: settings,
        'profile.updatedAt': Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating user settings:', error);
      throw error;
    }
  }

  // Verify user email
  async verifyEmail(userId: string): Promise<void> {
    try {
      const docRef = doc(db, this.collection, userId);
      await updateDoc(docRef, {
        'profile.verification.emailVerified': true,
        'profile.updatedAt': Timestamp.now()
      });
    } catch (error) {
      console.error('Error verifying email:', error);
      throw error;
    }
  }

  // Verify user phone
  async verifyPhone(userId: string): Promise<void> {
    try {
      const docRef = doc(db, this.collection, userId);
      await updateDoc(docRef, {
        'profile.verification.phoneVerified': true,
        'profile.updatedAt': Timestamp.now()
      });
    } catch (error) {
      console.error('Error verifying phone:', error);
      throw error;
    }
  }

  // Get brokers
  async getBrokers(): Promise<User[]> {
    try {
      const q = query(
        collection(db, this.collection),
        where('profile.userType', '==', 'broker'),
        where('profile.verification.identityVerified', '==', true),
        orderBy('profile.createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const brokers: User[] = [];
      
      querySnapshot.forEach((doc) => {
        brokers.push({ id: doc.id, ...doc.data() } as User);
      });

      return brokers;
    } catch (error) {
      console.error('Error getting brokers:', error);
      throw error;
    }
  }

  // Get active users (users active in last 30 days)
  async getActiveUsers(): Promise<User[]> {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const q = query(
        collection(db, this.collection),
        where('profile.lastActive', '>=', Timestamp.fromDate(thirtyDaysAgo)),
        orderBy('profile.lastActive', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const users: User[] = [];
      
      querySnapshot.forEach((doc) => {
        users.push({ id: doc.id, ...doc.data() } as User);
      });

      return users;
    } catch (error) {
      console.error('Error getting active users:', error);
      throw error;
    }
  }
}

export const userService = new UserService();
export default userService; 